import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getApiErrorCode,
  getApiErrorMessage,
  getApiErrorStatus,
  shouldRetryRequest,
} from "api/apiError";
import {
  confirmPublicPayment,
  confirmStatementPayment,
  fetchPublicInvoice,
  fetchPublicStatement,
  GatewayReturn,
  PublicInvoice,
  PublicStatement,
  publicPayKeys,
  startPublicCheckout,
  startStatementCheckout,
} from "api/billingPublic";
import { PayBaseline, PayReturnOutcome, StatementReturnOutcome } from "./types";

const POLL_INTERVAL_MS = 3000;
const POLL_WINDOW_MS = 60000;
const BASELINE_PREFIX = "sap-pay-baseline:";

const saveBaseline = (token: string, invoice: PublicInvoice | undefined) => {
  if (!invoice) {
    return;
  }
  try {
    const baseline: PayBaseline = { amountPaid: invoice.amountPaid, at: Date.now() };
    window.sessionStorage.setItem(`${BASELINE_PREFIX}${token}`, JSON.stringify(baseline));
  } catch (err) {
    // Storage can be unavailable (private mode); the return page then relies on the status only.
  }
};

const readBaseline = (token: string): PayBaseline | null => {
  try {
    const raw = window.sessionStorage.getItem(`${BASELINE_PREFIX}${token}`);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as Partial<PayBaseline>;
    return typeof parsed.amountPaid === "number" && typeof parsed.at === "number"
      ? { amountPaid: parsed.amountPaid, at: parsed.at }
      : null;
  } catch (err) {
    return null;
  }
};

/** Friendly text for public API failures (never shows internals). */
export const describePublicError = (err: unknown): string => {
  const status = getApiErrorStatus(err);
  const code = getApiErrorCode(err);
  if (code === "BILLING_PAY_TOKEN_INVALID" || status === 404) {
    return "This payment link isn't valid. Please use the latest link from your invoice email.";
  }
  if (status === 429) {
    return "Too many attempts. Please wait a minute and try again.";
  }
  switch (code) {
    case "BILLING_NOTHING_DUE":
      return "Nothing is due on this invoice any more.";
    case "BILLING_CHECKOUT_IN_PROGRESS":
      return "A payment is already being set up for this invoice. Please wait a few seconds and try again.";
    case "BILLING_GATEWAY_NOT_CONFIGURED":
      return "Online payment isn't available right now. Please contact Meta16 Labs to pay another way.";
    case "BILLING_GATEWAY_ERROR":
      return "The payment provider isn't responding. Please try again in a minute.";
    default:
      return getApiErrorMessage(err, "Something went wrong. Please try again.");
  }
};

/** /pay/:token — invoice summary + "Pay securely" checkout. */
export const usePayPageData = (token: string) => {
  const [redirecting, setRedirecting] = React.useState<boolean>(false);
  const query = useQuery({
    queryKey: publicPayKeys.invoice(token),
    queryFn: () => fetchPublicInvoice(token),
    retry: shouldRetryRequest,
    enabled: Boolean(token),
  });
  const { data: invoice, refetch } = query;

  const checkout = useMutation<{ url: string }, unknown, void>({
    mutationFn: () => startPublicCheckout(token),
    onSuccess: ({ url }) => {
      saveBaseline(token, invoice);
      setRedirecting(true);
      window.location.assign(url);
    },
    onError: (err) => {
      if (getApiErrorCode(err) === "BILLING_NOTHING_DUE") {
        refetch();
      }
    },
  });

  // Back from the gateway, browsers may restore this page from the back/forward cache with
  // "Opening secure checkout…" still showing: reset it and reload the invoice (it may be paid).
  React.useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setRedirecting(false);
        refetch();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [refetch]);

  return {
    query,
    invoice,
    checkout,
    redirecting,
    checkoutError: checkout.error ? describePublicError(checkout.error) : null,
  };
};

/** The signed details the gateway appends to the return URL, when all of them are there. */
export const readGatewayReturn = (params: URLSearchParams): GatewayReturn | null => {
  const read = (key: string) => (params.get(key) || "").trim();
  const gatewayReturn: GatewayReturn = {
    razorpay_payment_id: read("razorpay_payment_id"),
    razorpay_payment_link_id: read("razorpay_payment_link_id"),
    razorpay_payment_link_reference_id: read("razorpay_payment_link_reference_id"),
    razorpay_payment_link_status: read("razorpay_payment_link_status"),
    razorpay_signature: read("razorpay_signature"),
  };
  return gatewayReturn.razorpay_payment_id &&
    gatewayReturn.razorpay_payment_link_id &&
    gatewayReturn.razorpay_payment_link_status &&
    gatewayReturn.razorpay_signature
    ? gatewayReturn
    : null;
};

/**
 * /pay/return?token=… — first asks SAP to confirm the payment with the gateway's signed return
 * details (recorded within seconds, no webhook needed), then polls the invoice every 3 s for up to
 * 60 s as a fallback.
 */
export const usePayReturnData = (token: string, gatewayReturn: GatewayReturn | null) => {
  const queryClient = useQueryClient();
  const baseline = React.useMemo(() => (token ? readBaseline(token) : null), [token]);
  const [round, setRound] = React.useState<number>(0);
  const [timedOut, setTimedOut] = React.useState<boolean>(false);

  React.useEffect(() => {
    setTimedOut(false);
    const handle = window.setTimeout(() => setTimedOut(true), POLL_WINDOW_MS);
    return () => window.clearTimeout(handle);
  }, [round]);

  const isSettled = React.useCallback(
    (invoice: PublicInvoice | undefined) =>
      Boolean(
        invoice &&
          (invoice.status === "paid" ||
            invoice.status === "void" ||
            (baseline !== null && invoice.amountPaid > baseline.amountPaid))
      ),
    [baseline]
  );

  const returnKey = React.useMemo(() => [...publicPayKeys.invoice(token), "return"], [token]);
  const confirmation = useQuery({
    queryKey: [...publicPayKeys.invoice(token), "confirm", gatewayReturn?.razorpay_payment_id],
    queryFn: () => confirmPublicPayment({ token, ...(gatewayReturn as GatewayReturn) }),
    enabled: Boolean(token && gatewayReturn),
    retry: 2,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: (result) => queryClient.setQueryData(returnKey, result.invoice),
  });
  const confirming = Boolean(gatewayReturn) && confirmation.isLoading;

  const query = useQuery({
    queryKey: returnKey,
    queryFn: () => fetchPublicInvoice(token),
    // Polling waits for the confirmation; if that fails it still finds the payment later.
    enabled: Boolean(token) && !confirming,
    retry: shouldRetryRequest,
    refetchInterval: (data: PublicInvoice | undefined) =>
      isSettled(data) || timedOut ? false : POLL_INTERVAL_MS,
  });

  const invoice = query.data || (confirmation.data ? confirmation.data.invoice : undefined);
  let outcome: PayReturnOutcome;
  if (!token) {
    outcome = "missing_token";
  } else if (confirming) {
    outcome = "confirming";
  } else if (query.isLoading && !invoice) {
    outcome = "loading";
  } else if (!invoice) {
    // Keep showing the last good answer if a later poll fails; only fail without any data.
    outcome = getApiErrorStatus(query.error) === 404 ? "invalid" : "error";
  } else if (invoice.status === "paid") {
    outcome = "paid";
  } else if (invoice.status === "void") {
    outcome = "void";
  } else if (baseline !== null && invoice.amountPaid > baseline.amountPaid) {
    outcome = "partial";
  } else {
    outcome = timedOut ? "unconfirmed" : "confirming";
  }

  const checkAgain = () => {
    setRound((value) => value + 1);
    query.refetch();
  };
  const receiptNumber = confirmation.data ? confirmation.data.receiptNumber : null;
  const receiptStatus = confirmation.data ? confirmation.data.receiptStatus : null;

  return { outcome, invoice, query, checkAgain, receiptNumber, receiptStatus };
};

/** /pay/s/:token — everything due now and one "Pay" button (what TeleOpd's "Pay now" opens). */
export const useStatementPageData = (token: string) => {
  const [redirecting, setRedirecting] = React.useState<boolean>(false);
  const query = useQuery({
    queryKey: publicPayKeys.statement(token),
    queryFn: () => fetchPublicStatement(token),
    retry: shouldRetryRequest,
    enabled: Boolean(token),
  });
  const { refetch } = query;

  const checkout = useMutation<{ url: string }, unknown, void>({
    mutationFn: () => startStatementCheckout(token),
    onSuccess: ({ url }) => {
      setRedirecting(true);
      window.location.assign(url);
    },
    onError: (err) => {
      if (getApiErrorCode(err) === "BILLING_NOTHING_DUE") {
        refetch();
      }
    },
  });

  // Back from the gateway via the browser's back/forward cache: reset and reload what is due.
  React.useEffect(() => {
    const onPageShow = (event: PageTransitionEvent) => {
      if (event.persisted) {
        setRedirecting(false);
        refetch();
      }
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, [refetch]);

  return {
    query,
    statement: query.data,
    checkout,
    redirecting,
    checkoutError: checkout.error ? describePublicError(checkout.error) : null,
  };
};

/**
 * /pay/return?statement=… — confirms the payment with the gateway's signed return details, then
 * polls the statement every 3 s for up to 60 s until nothing is due.
 */
export const useStatementReturnData = (token: string, gatewayReturn: GatewayReturn | null) => {
  const queryClient = useQueryClient();
  const [round, setRound] = React.useState<number>(0);
  const [timedOut, setTimedOut] = React.useState<boolean>(false);
  const returnKey = React.useMemo(() => [...publicPayKeys.statement(token), "return"], [token]);

  React.useEffect(() => {
    setTimedOut(false);
    const handle = window.setTimeout(() => setTimedOut(true), POLL_WINDOW_MS);
    return () => window.clearTimeout(handle);
  }, [round]);

  const confirmation = useQuery({
    queryKey: [...publicPayKeys.statement(token), "confirm", gatewayReturn?.razorpay_payment_id],
    queryFn: () =>
      confirmStatementPayment({ statement: token, ...(gatewayReturn as GatewayReturn) }),
    enabled: Boolean(token && gatewayReturn),
    retry: 2,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: (result) => queryClient.setQueryData(returnKey, result.statement),
  });
  const confirming = Boolean(gatewayReturn) && confirmation.isLoading;

  const query = useQuery({
    queryKey: returnKey,
    queryFn: () => fetchPublicStatement(token),
    enabled: Boolean(token) && !confirming,
    retry: shouldRetryRequest,
    refetchInterval: (data: PublicStatement | undefined) =>
      (data && data.amountDue === 0) || timedOut ? false : POLL_INTERVAL_MS,
  });

  const statement = query.data || (confirmation.data ? confirmation.data.statement : undefined);
  const confirmed = Boolean(confirmation.data && confirmation.data.confirmed);
  let outcome: StatementReturnOutcome;
  if (!token) {
    outcome = "missing_token";
  } else if (confirming) {
    outcome = "confirming";
  } else if (query.isLoading && !statement) {
    outcome = "loading";
  } else if (!statement) {
    outcome = getApiErrorStatus(query.error) === 404 ? "invalid" : "error";
  } else if (statement.amountDue === 0) {
    outcome = "paid";
  } else if (confirmed) {
    // Paid, but more is due now than this payment covered (e.g. a new invoice meanwhile).
    outcome = "partial";
  } else {
    outcome = timedOut ? "unconfirmed" : "confirming";
  }

  const checkAgain = () => {
    setRound((value) => value + 1);
    query.refetch();
  };
  const receiptNumber = confirmation.data ? confirmation.data.receiptNumber : null;
  const receiptStatus = confirmation.data ? confirmation.data.receiptStatus : null;

  return { outcome, statement, query, checkAgain, receiptNumber, receiptStatus };
};
